import {VehicleProject, VehicleProjectMaster} from 'types';
import {mapToRef} from 'helpers';
import {GetVehicleProjectOverviewPageDataQuery} from '../hooks';

type query = NonNullable<
	GetVehicleProjectOverviewPageDataQuery['vehicleProjectMasterWithProjectDetails']
>;

export type CombinedVehicleProjectItem = VehicleProject & VehicleProjectMaster;

/*
 * Combines vehicle project masters with their details.
 */
export function buildCombinedVehicleProjects(
	mastersWithProjects?: query,
): CombinedVehicleProjectItem[] {
	const projectList = mastersWithProjects ?? [];
	return projectList
		.map(mwp => {
			const master = mwp.vehicleProjectMaster;
			const details = mwp.vehicleProjects;
			const baseName = `${master.modelSeries} ${master.generation}`;
			const detail = details && details.length > 0 ? details[0] : undefined; // Take the first project detail if exists
			const combined: CombinedVehicleProjectItem = {
				// VehicleProject Master
				id: master.id,
				modelSeries: master.modelSeries,
				generation: master.generation,
				derivates: master.derivates,
				sopVsi: master.sopVsi,
				eopVsi: master.eopVsi,
				name: baseName,
				isManuallyCreated: master.isManuallyCreated,
				requirementCounts: master.requirementCounts,
				createdBy: master.createdBy,
				modifiedBy: master.modifiedBy,
				transferStatus: master.transferStatus,
				lastTransfer: master.lastTransfer,

				// VehicleProject Detail
				derivative: detail?.derivative ?? null,
				p10Vsi: detail?.p10Vsi ?? null,
				posEtVsi: detail?.posEtVsi ?? null,
				posVsi: detail?.posVsi ?? null,
				sosVsi: detail?.sosVsi ?? null,
				sysgenVsi: detail?.sysgenVsi ?? null,
				typStartVsi: detail?.typStartVsi ?? null,
				wvtaVsi: detail?.wvtaVsi ?? null,
				detVsi: detail?.detVsi ?? null,
				euLegislation: detail?.euLegislation ?? null,
				isElectro: detail?.isElectro ?? null,
				modelYear: detail?.modelYear ?? null,
				activeTypeMarkets: detail?.activeTypeMarkets ?? [],
				bodyworkVariants: detail?.bodyworkVariants ?? [],
				driveVariants: detail?.driveVariants ?? [],
				engineVariants: detail?.engineVariants ?? [],
				technology: detail?.technology ?? null,
				gearboxVariants: detail?.gearboxVariants ?? [],
				anonymous: detail?.anonymous ?? false,
				willBeAnonymized: detail?.willBeAnonymized ?? false,
				deletionPeriod: detail?.deletionPeriod ?? 0,
				markets: [],
				assignedRequirementRefs: [],
			};
			return combined;
		})
		.sort((a, b) =>
			`${a.modelSeries}${a.generation}`.localeCompare(
				`${b.modelSeries}${b.generation}`,
			),
		);
}

/**
 * Maps a VehicleProject instance to mutation input.
 */
export function mapVehicleProjectToInput(vehicleProject: VehicleProject) {
	return {
		derivative: vehicleProject.derivative,
		generation: vehicleProject.generation,
		modelSeries: vehicleProject.modelSeries,
		modelYear: vehicleProject.modelYear
			? parseInt(vehicleProject.modelYear as any, 10)
			: undefined,
		euLegislation: vehicleProject.euLegislation,
		isElectro: vehicleProject.isElectro,
		eopVsi: vehicleProject.eopVsi,
		detVsi: vehicleProject.detVsi,
		p10Vsi: vehicleProject.p10Vsi,
		typStartVsi: vehicleProject.typStartVsi,
		sysgenVsi: vehicleProject.sysgenVsi,
		sopVsi: vehicleProject.sopVsi,
		sosVsi: vehicleProject.sosVsi,
		wvtaVsi: vehicleProject.wvtaVsi,
		posEtVsi: vehicleProject.posEtVsi,
		posVsi: vehicleProject.posVsi,
		activeTypeMarketRefs: mapToRef(vehicleProject?.activeTypeMarkets),
		engineVariantRefs: mapToRef(vehicleProject?.engineVariants),
		driveVariantRefs: mapToRef(vehicleProject?.driveVariants),
		bodyworkVariantRefs: mapToRef(vehicleProject?.bodyworkVariants),
		gearboxVariantRefs: mapToRef(vehicleProject?.gearboxVariants),
		marketRefs: mapToRef(vehicleProject?.markets),
		deletionPeriod: vehicleProject.deletionPeriod,
		willBeAnonymized: vehicleProject.willBeAnonymized,
	};
}
